import React, { useEffect, useCallback } from "react"
import Tippy, { TippyProps } from "@tippyjs/react"
import { sticky } from "tippy.js"

import useBreakpoint from "../../hooks/useBreakpoint"
import Modal, { ModalPropsType } from "../Modal"
import LazyTippy from "./LazyTippy"
import { zIndexes } from "../../tokens"

export type PopoverPropsType = {
  children?: React.ReactNode
  isOpen: boolean
  close: () => void
  "aria-label"?: string
  bodySpacing?: boolean
  onShow?: () => void
  onHidden?: () => void
  // Desktop props
  targetRef: TippyProps["reference"]
  arrow?: boolean
  placement?: TippyProps["placement"]
  flip?: boolean
  theme?: string
  distance?: number
  zIndex?: number
  appendToBody?: boolean
  fitContent?: boolean
  /**
   * __Only for desktop popover__
   *
   * Allow to render the content only when the popover is mounted in the DOM
   * Useful to render async content in the popover
   */
  lazy?: boolean
}

const ESC_KEY_CODE = 27

const defaultTippyProps: Omit<TippyProps, "content" | "children" | "visible"> =
  {
    animation: "shift-away-subtle",
    interactive: true,
    interactiveBorder: 10,
    delay: [0, 0],
    duration: [200, 150],
    maxWidth: 500,
    sticky: true,
    plugins: [sticky],
    theme: "light cobalt-popover",
    inertia: true,
  }

const MobilePopover = ({
  isOpen,
  children,
  ariaLabel = "Popover mobile modal",
  onDismiss,
  bodySpacing,
  onShow,
  onHidden,
}: {
  children?: React.ReactNode
  ariaLabel?: ModalPropsType["aria-label"]
  isOpen: ModalPropsType["isOpen"]
  onDismiss?: () => void
  bodySpacing: ModalPropsType["bodySpacing"]
  onShow: ModalPropsType["onShow"]
  onHidden: ModalPropsType["onHidden"]
}) => {
  return (
    <Modal
      aria-label={ariaLabel}
      isOpen={isOpen}
      onDismissAttempt={onDismiss}
      bodySpacing={bodySpacing}
      onShow={onShow}
      onHidden={onHidden}
    >
      {children}
    </Modal>
  )
}

const Popover = ({
  isOpen,
  targetRef,
  close,
  "aria-label": ariaLabel,
  children,
  bodySpacing = true,
  onShow,
  onHidden,
  // Desktop props
  arrow = false,
  placement = "bottom-start",
  flip = true,
  theme = "",
  distance = 8,
  zIndex = zIndexes.dropdown,
  appendToBody = false,
  fitContent = false,
  lazy,
}: PopoverPropsType) => {
  const { breakpoint } = useBreakpoint()
  const isMobile = breakpoint === "sm" || breakpoint === "xs"

  const escListener = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === ESC_KEY_CODE && isOpen) close()
    },
    [isOpen, close]
  )

  useEffect(() => {
    window.addEventListener("keydown", escListener)

    return () => window.removeEventListener("keydown", escListener)
  }, [escListener])

  const onTippyClose = () => {
    if (onHidden) onHidden()
  }

  const onTippyOpen = () => {
    if (onShow) onShow()
  }

  const TippyComp = lazy ? LazyTippy : Tippy

  return isMobile ? (
    <MobilePopover
      isOpen={isOpen}
      onDismiss={close}
      bodySpacing={bodySpacing}
      ariaLabel={ariaLabel}
      onShow={onShow}
      onHidden={onHidden}
    >
      {children}
    </MobilePopover>
  ) : (
    <TippyComp
      content={
        bodySpacing ? (
          <div className="cobalt-popover--bodySpacing">{children}</div>
        ) : (
          children
        )
      }
      {...defaultTippyProps}
      visible={isOpen}
      reference={targetRef}
      theme={`${defaultTippyProps.theme} ${theme} ${
        arrow ? "cobalt-popover--withArrow" : ""
      } ${fitContent ? "cobalt-popover--fitContent" : ""}`}
      offset={[0, distance]}
      {...{ arrow, placement, zIndex }}
      popperOptions={{ modifiers: [{ name: "flip", enabled: flip }] }}
      onHidden={onTippyClose}
      onShow={onTippyOpen}
      onClickOutside={close}
      appendTo={appendToBody ? document.body : "parent"}
    />
  )
}

export default Popover
